import { useMemo } from 'react';
import { apiRootOf } from './engagement-channel';
import { destinationChannel } from './destination-channel';
import { downloadDocuments, packagesReady } from './document-downloads';
import { emailChannel } from './email-channel';
import { downloadExported } from './exported-downloads';
import { exportChannel } from './export-channel';
import { fetchExportFacts } from './export-facts';
import { filingPlanChannel } from './filing-plan-channel';
import {
  fetchEmailIntake,
  fetchJobProgress,
  fetchRetention,
  isJobChannel,
  saveRetention,
} from './job-channel';
import { mobileChannel } from './mobile-channel';
import { reviewLeaseChannel } from './review-leases';
import { watchedFolderChannel } from './watched-folder-channel';
import type { CapturePointWorkbenchProps } from './CapturePointWorkbench';
import type {
  PaigeIntakeKind,
  PaigeRetention,
  PaigeUploadProgress,
  PaigeWorkbenchBridge,
} from './paige.global';

/** What the mount itself contributes to the bridge: its sheets and its first-screen signal. */
export interface MountHooks {
  showIntake: (kind: PaigeIntakeKind) => void;
  showUpload: (progress: PaigeUploadProgress | null) => void;
  opened: () => void;
  /** The company's job limit refused a new job: the host shows the notice. */
  jobLimit?: (limit: number) => void;
}

/**
 * The bridge Paige hands the ported screens (`window.paigeWorkbench`): the routes around the job,
 * who is signed in, the link the page was opened with, and on a job's channel everything only that
 * job answers. One object per change of its inputs, so the adapter is not rebuilt every render.
 */
export function useBridge(
  props: CapturePointWorkbenchProps,
  mount: MountHooks,
): PaigeWorkbenchBridge {
  const { engineOrigin = '', tenant, onLeave, onCreated, appLink, createStart } = props;
  const routes = useJobRoutes(props.onNewJob, props.onOpenJob);
  const people = usePeople(props);
  const { showIntake, showUpload, opened, jobLimit } = mount;
  return useMemo<PaigeWorkbenchBridge>(
    () => ({
      leave: onLeave ?? (() => undefined),
      createOnOpen: Boolean(onCreated),
      created: onCreated,
      ...routes,
      showIntake,
      upload: showUpload,
      opened,
      ...(jobLimit ? { jobLimit } : {}),
      ...(appLink ? { appLink } : {}),
      ...(createStart ? { createStart } : {}),
      ...people,
      ...jobChannelBridge(engineOrigin, tenant),
    }),
    // prettier-ignore
    [onLeave, onCreated, routes, engineOrigin, tenant, people, showIntake, showUpload, opened, jobLimit, appLink, createStart],
  );
}

/** Who is signed in, for the bridge: the person on the leases and the account door, and whether they run jobs and the company. */
type People = Pick<
  PaigeWorkbenchBridge,
  'user' | 'person' | 'runsJobs' | 'runsCompany' | 'readOnly'
>;

function usePeople(props: CapturePointWorkbenchProps): People {
  const { user, person, runsJobs, runsCompany, readOnly } = props;
  return useMemo(
    () => ({
      ...(user ? { user } : {}),
      ...(person ? { person } : {}),
      ...(runsJobs === undefined ? {} : { runsJobs }),
      ...(runsCompany === undefined ? {} : { runsCompany }),
      ...(readOnly ? { readOnly } : {}),
    }),
    [user, person, runsJobs, runsCompany, readOnly],
  );
}

/** Where New job and another job lead from a job's own route; neither on the new-job route. */
type JobRoutes = Pick<PaigeWorkbenchBridge, 'newJob' | 'openJob'>;

function useJobRoutes(
  newJob: ((mode?: string) => void) | undefined,
  openJob: ((workspaceJobId: string) => void) | undefined,
): JobRoutes {
  return useMemo(
    () => ({ ...(newJob ? { newJob } : {}), ...(openJob ? { openJob } : {}) }),
    [newJob, openJob],
  );
}

/** What only a job's channel answers; on the tenant's channel there is no job yet, so nothing. */
function jobChannelBridge(
  engineOrigin: string,
  tenant: string | undefined,
): Partial<PaigeWorkbenchBridge> {
  if (!isJobChannel(engineOrigin)) return {};
  return {
    emailIntake: () => fetchEmailIntake(engineOrigin, tenant),
    email: emailChannel(engineOrigin, tenant),
    watchedFolder: watchedFolderChannel(engineOrigin, tenant),
    mobile: mobileChannel(engineOrigin, tenant),
    jobProgress: () => fetchJobProgress(engineOrigin, tenant),
    retention: () => fetchRetention(engineOrigin, tenant),
    setRetention: (value: PaigeRetention) => saveRetention(engineOrigin, tenant, value),
    destinations: destinationChannel(engineOrigin, tenant),
    exports: exportChannel(engineOrigin, tenant),
    filingPlan: filingPlanChannel(engineOrigin, tenant),
    exportFacts: () => fetchExportFacts(engineOrigin, tenant),
    downloadDocuments: (documentIds, zipName) =>
      downloadDocuments(documentIds, zipName, {
        apiOrigin: apiRootOf(engineOrigin, undefined),
        tenant,
      }),
    downloadExported: (zipName) =>
      downloadExported(engineOrigin, apiRootOf(engineOrigin, undefined), tenant, zipName),
    documentsExported: (documentIds) =>
      packagesReady(documentIds, { apiOrigin: apiRootOf(engineOrigin, undefined), tenant }),
    reviewLeases: reviewLeaseChannel(engineOrigin, tenant) ?? undefined,
  };
}
